import React, { useState, useRef, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FastForwardIcon from '@mui/icons-material/FastForward';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { useNavigate } from 'react-router-dom';
import { SeverityChip } from 'components/shared/SeverityChip';
import { ErrorState } from 'components/shared/ErrorState';
import type { IncidentStatus } from '../../types/canvision.types';
import { ROUTE_SCHEDULER } from 'constants/routerPath';
import { useIncidentDetail } from '../../hooks/useIncidentDetail';
import IncidentStatusBadge from '../shared/IncidentStatusBadge';
import IncidentTimeline from './IncidentTimeline';
import IncidentNotes from './IncidentNotes';
import IncidentActions from './IncidentActions';
import IncidentChecklist from './IncidentChecklist';
import VideoPlayer from '../VideoPlayer/VideoPlayer';

interface IncidentDetailProps {
  incidentId: number | null;
  open: boolean;
  onClose: () => void;
  onStatusChange?: () => void;
}

// Format date for display
function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function IncidentDetail({ incidentId, open, onClose, onStatusChange }: IncidentDetailProps) {
  const navigate = useNavigate();
  const { incident, loading, error, updateStatus, addNote, refetch } = useIncidentDetail({
    incidentId,
    enabled: open && incidentId !== null,
  });

  const [showLiveView, setShowLiveView] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['details', 'timeline', 'notes', 'actions']);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const handleStatusChange = async (status: IncidentStatus, notes?: string) => {
    await updateStatus(status, notes);
    onStatusChange?.();
  };

  const handleViewPatrol = useCallback((patrolId: number) => {
    navigate(`${ROUTE_SCHEDULER}?patrolId=${patrolId}`);
  }, [navigate]);

  const handleFullscreen = useCallback(() => {
    const el = videoContainerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  // Build video URL for clip or live view
  const getClipUrl = () => {
    if (!incident) return null;
    return incident.clip_url || null;
  };

  const getLiveUrl = () => null; // Video unavailable until go2rtc migration

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500, md: 600 },
          maxWidth: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Box>
          <Typography variant="h6">
            Incident #{incidentId}
          </Typography>
          {incident && (
            <Typography variant="body2" color="text.secondary">
              {incident.title}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {loading && !incident && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ m: 2 }}>
            <ErrorState
              title="Failed to load incident"
              message={error}
              onRetry={refetch}
              size="small"
            />
          </Box>
        )}

        {incident && (
          <>
            {/* Video Player Section */}
            <Box ref={videoContainerRef} sx={{ bgcolor: 'black', position: 'relative' }}>
              <Box sx={{ aspectRatio: '16/9' }}>
                {showLiveView && getLiveUrl() ? (
                  <VideoPlayer
                    streamUrl={getLiveUrl()!}
                    streamType="hls"
                    poster={incident.thumbnail_url || undefined}
                  />
                ) : getClipUrl() ? (
                  <VideoPlayer
                    streamUrl={getClipUrl()!}
                    streamType="hls"
                    poster={incident.thumbnail_url || undefined}
                  />
                ) : incident.thumbnail_url && !thumbnailError ? (
                  <Box
                    component="img"
                    src={incident.thumbnail_url}
                    alt="Incident thumbnail"
                    onError={() => setThumbnailError(true)}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.900',
                    }}
                  >
                    <Typography color="grey.500">No video available</Typography>
                  </Box>
                )}
              </Box>

              {/* Video Controls */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: 'grey.900',
                }}
              >
                {incident.clip_start_time && (
                  <Typography variant="caption" color="grey.400">
                    Clip: {new Date(incident.clip_start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    {incident.clip_end_time && ` - ${new Date(incident.clip_end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  startIcon={<LiveTvIcon />}
                  onClick={() => setShowLiveView(!showLiveView)}
                  sx={{
                    color: showLiveView ? 'error.main' : 'grey.400',
                    borderColor: showLiveView ? 'error.main' : 'grey.600',
                  }}
                  variant="outlined"
                >
                  Live
                </Button>
                <IconButton size="small" sx={{ color: 'grey.400' }} onClick={handleFullscreen}>
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Details Section */}
            <Accordion
              expanded={expandedSections.includes('details')}
              onChange={() => toggleSection('details')}
              disableGutters
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="medium">Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <IncidentStatusBadge status={incident.status} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Severity</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <SeverityChip severity={incident.severity} size="small" />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Typography variant="body2">{incident.incident_type}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created by</Typography>
                    <Typography variant="body2">
                      {incident.created_by_name
                        ? incident.created_by_name
                        : incident.created_by
                          ? `Operator #${incident.created_by}`
                          : 'Unknown'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Camera</Typography>
                    <Typography variant="body2">
                      {incident.camera_display_name || incident.camera_name || 'Unknown'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Site</Typography>
                    <Typography variant="body2">
                      {incident.site_display_name || incident.site_name || 'Unknown'}
                    </Typography>
                  </Box>
                  {incident.customer_name && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Customer</Typography>
                      <Typography variant="body2">{incident.customer_name}</Typography>
                    </Box>
                  )}
                  {incident.address_title && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Address</Typography>
                      <Typography variant="body2">{incident.address_title}</Typography>
                    </Box>
                  )}
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2">{formatDateTime(incident.created_at)}</Typography>
                  </Box>
                  {incident.description && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" color="text.secondary">Description</Typography>
                      <Typography variant="body2">{incident.description}</Typography>
                    </Box>
                  )}
                  {incident.patrol && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" color="text.secondary">Patrol</Typography>
                      <Typography variant="body2">
                        {incident.patrol.guard_name || 'Guard'} - {incident.patrol.status}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Divider />

            {/* Timeline Section */}
            <Accordion
              expanded={expandedSections.includes('timeline')}
              onChange={() => toggleSection('timeline')}
              disableGutters
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="medium">Timeline</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <IncidentTimeline timeline={incident.timeline} />
              </AccordionDetails>
            </Accordion>

            <Divider />

            {/* Notes Section */}
            <Accordion
              expanded={expandedSections.includes('notes')}
              onChange={() => toggleSection('notes')}
              disableGutters
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">Notes</Typography>
                  {incident.notes && incident.notes.length > 0 && (
                    <Chip label={incident.notes.length} size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <IncidentNotes notes={incident.notes} onAddNote={addNote} />
              </AccordionDetails>
            </Accordion>

            {/* Checklist Section (only if checklist assigned) */}
            {incident.checklist_id && (
              <>
                <Divider />
                <Accordion
                  expanded={expandedSections.includes('checklist')}
                  onChange={() => toggleSection('checklist')}
                  disableGutters
                  elevation={0}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChecklistRtlIcon fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="medium">Checklist</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <IncidentChecklist incidentId={incident.id} />
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            <Divider />

            {/* Actions Section */}
            <Accordion
              expanded={expandedSections.includes('actions')}
              onChange={() => toggleSection('actions')}
              disableGutters
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="medium">Actions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <IncidentActions
                  incident={incident}
                  onStatusChange={handleStatusChange}
                  onViewPatrol={handleViewPatrol}
                />
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Box>
    </Drawer>
  );
}

export default IncidentDetail;
